import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { constants } from '../constants';
/**
 * Get policies from session storage
 */
export const getPoliciesFromSession = () => {
    try {
        const policiesStr = sessionStorage.getItem('policy');
        if (!policiesStr)
            return [];
        const parsed = JSON.parse(policiesStr);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch (error) {
        console.error('Failed to parse policies from session storage:', error);
        return [];
    }
};
/**
 * Component that controls rendering/behavior of children based on user permissions
 *
 * @example
 * // Hide element when not authorized
 * <PermissionGate action="Platform:DeleteData">
 *   <Button>Delete</Button>
 * </PermissionGate>
 *
 * @example
 * // Disable element when not authorized
 * <PermissionGate action="Platform:EditData" behavior="disable">
 *   <Button>Edit</Button>
 * </PermissionGate>
 *
 * @example
 * // Grey out element when not authorized
 * <PermissionGate action="Platform:ViewData" behavior="grey" unauthorizedTooltip="You don't have permission">
 *   <Typography>Sensitive Data</Typography>
 * </PermissionGate>
 */
/**
 * Check if action is authorized based on policies
 */
const checkActionAuthorization = (action, policies) => {
    if (!policies || policies.length === 0) {
        return false;
    }
    let hasAllowPolicy = false;
    for (const policy of policies) {
        const resourceActions = policy.resource_action || [];
        for (const resourceAction of resourceActions) {
            const actions = resourceAction.action || [];
            // Check if action matches exactly or via wildcard pattern
            const isMatch = actions.some((policyAction) => {
                const lowerPolicyAction = policyAction.toLowerCase();
                const lowerAction = action.toLowerCase();
                // Exact match
                if (lowerPolicyAction === lowerAction) {
                    return true;
                }
                // Wildcard match: convert "Platform:*" to regex "^platform:.*$"
                if (lowerPolicyAction.includes('*')) {
                    const regexPattern = '^' + lowerPolicyAction.replaceAll('*', '.*') + '$';
                    const regex = new RegExp(regexPattern);
                    return regex.test(lowerAction);
                }
                return false;
            });
            if (isMatch) {
                if (policy.effect === 'allow') {
                    hasAllowPolicy = true;
                }
                else if (policy.effect === 'deny') {
                    // Early return if we find a deny policy - deny always wins
                    return false;
                }
            }
        }
    }
    return hasAllowPolicy;
};
export const PermissionGate = ({ action, policies: policiesProp, children, behavior = 'hide', fallback = null, unauthorizedTooltip = constants.permission_gate_unauthorized_tooltip, requireAll = true }) => {
    // Get policies from props or session storage
    const policies = policiesProp ?? getPoliciesFromSession();
    const authorized = useMemo(() => {
        const actions = Array.isArray(action) ? action : [action];
        if (requireAll) {
            // User must have ALL actions
            return actions.every((act) => checkActionAuthorization(act, policies));
        }
        else {
            // User needs ANY action
            return actions.some((act) => checkActionAuthorization(act, policies));
        }
    }, [action, policies, requireAll]);
    // User is authorized - render children normally
    if (authorized) {
        return _jsx(_Fragment, { children: children });
    }
    // User is NOT authorized - apply behavior
    switch (behavior) {
        case 'hide':
            return _jsx(_Fragment, { children: fallback });
        case 'disable':
            return (_jsx(Tooltip, { title: unauthorizedTooltip, arrow: true, children: _jsx("span", { children: React.Children.map(children, (child) => {
                        if (React.isValidElement(child)) {
                            return React.cloneElement(child, {
                                disabled: true
                            });
                        }
                        return child;
                    }) }) }));
        case 'grey':
            return (_jsx(Tooltip, { title: unauthorizedTooltip, arrow: true, children: _jsx(Box, { sx: {
                        opacity: 0.4,
                        pointerEvents: 'none',
                        cursor: 'not-allowed'
                    }, children: children }) }));
        default:
            return _jsx(_Fragment, { children: fallback });
    }
};
